import { useState, useEffect, CSSProperties } from 'react';
import { clsx, styx } from 'modules/Utils';
import Colors from 'modules/Colors';
import Sty from 'modules/Sty';
import useScreen from 'Hooks/useScreen';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import useBrand from 'Hooks/useBrand';
import { CurrentBrand } from 'modules/Types';
import { AdConnectionType, SocialConnectType } from 'modules/Const';
import SocialConnectModal from 'components/shareable/SocialConnectModal';
import AdConnectionModal from 'components/shareable/AdConnectionModal';
import { copyConnectionBetweenBrands_api } from 'api/brandApi';
import BrandInfo from 'elements/BrandInfo';
import CardSymphony from 'elements/CardSymphony';
import {
    getBrandConnectionStatus,
    BrandConnectionStatus,
    PlatformConnection,
    ConnectionStatus,
    ConnectionPriority,
    getPlatformsNeedingAttention
} from 'helpers/connectionHelpers';
import ButtonSymphony from 'elements/ButtonSymphony';
import Icon from 'elements/Icon';
import Loader from 'elements/Loader';
import PageSymphony from 'elements/PageSymphony';
import millify from 'millify';
import { CURRENCY_CODE_DATA } from 'helpers/currencies';

interface TeamConnectionsPageProps {
    className?: string;
    style?: CSSProperties;
}

const TeamConnectionsPage = ({
    className,
    style
}: TeamConnectionsPageProps) => {
    const { mobileView, mStyle } = useScreen();
    const { organization, isOrganizationInitialized } = useOrganizationV2();
    const { loadedBrandsDict, setCurrentBrand } = useBrand();

    console.log('organization', organization);
    const [organizationBrands, setOrganizationBrands] = useState<CurrentBrand[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedBrand, setSelectedBrand] = useState<CurrentBrand | null>(null);
    const [selectedBrandStatus, setSelectedBrandStatus] = useState<BrandConnectionStatus | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedPlatform, setSelectedPlatform] = useState<SocialConnectType | null>(null);
    const [showSocialConnectModal, setShowSocialConnectModal] = useState(false);
    const [selectedAdConnectionType, setSelectedAdConnectionType] = useState<AdConnectionType | null>(null);
    const [originalAdConnectionType, setOriginalAdConnectionType] = useState<AdConnectionType | null>(null);
    const [showAdConnectionsModal, setShowAdConnectionsModal] = useState(false);

    useEffect(() => {
        if (isOrganizationInitialized && organization?.brands) {
            // Organization brands now come with complete data from backend
            // Fallback to loadedBrandsDict for any additional data if needed
            const brands = organization.brands
                .map(brandData => {
                    // Use organization brand data as primary source, with loadedBrandsDict as fallback
                    const loadedBrand = loadedBrandsDict?.[brandData.id];
                    return loadedBrand || brandData;
                })
                .filter(Boolean) as CurrentBrand[];

            setOrganizationBrands(brands);
            setIsLoading(false);
        }
    }, [organization, loadedBrandsDict, isOrganizationInitialized]);

    const handleViewDetails = (brand: CurrentBrand) => {
        const connectionStatus = getBrandConnectionStatus(brand);
        setSelectedBrand(brand);
        setSelectedBrandStatus(connectionStatus);
        setShowDetailsModal(true);
    };

    const handleConnectPlatform = (brand: CurrentBrand, platformType: SocialConnectType) => {
        setCurrentBrand(brand as any);
        setSelectedBrand(brand);
        setSelectedPlatform(platformType);
        setShowSocialConnectModal(true);
    };

    const handleConnectAdItem = (brand: CurrentBrand, type: AdConnectionType) => {
        setCurrentBrand(brand as any);
        setSelectedBrand(brand);
        
        // Check if Facebook profile is required but not connected
        const facebookConnectionTypes = [
            AdConnectionType.FACEBOOK_AD_ACCOUNT,
            AdConnectionType.FACEBOOK_PAGE,
            AdConnectionType.FACEBOOK_PIXEL,
            AdConnectionType.FACEBOOK_CONVERSIONS_API_TOKEN
        ];
        
        const requiresFacebookProfile = facebookConnectionTypes.includes(type);
        const hasFacebookProfile = Boolean(brand.connections?.logged_in_fb_user);
        
        if (requiresFacebookProfile && !hasFacebookProfile) {
            // Redirect to Facebook profile connection first
            setSelectedAdConnectionType(AdConnectionType.FACEBOOK_PROFILE);
            setOriginalAdConnectionType(type); // Remember what they originally wanted
        } else {
            setSelectedAdConnectionType(type);
            setOriginalAdConnectionType(null); // Not a prerequisite redirect
        }
        
        setShowAdConnectionsModal(true);
    };

    // Map AdConnectionType to API connection type strings
    const getConnectionTypeString = (type: AdConnectionType): string => {
        const typeMap: Record<AdConnectionType, string> = {
            [AdConnectionType.FACEBOOK_AD_ACCOUNT]: 'facebook_ad_account',
            [AdConnectionType.FACEBOOK_PAGE]: 'facebook_page',
            [AdConnectionType.FACEBOOK_PIXEL]: 'facebook_pixel',
            [AdConnectionType.FACEBOOK_CONVERSIONS_API_TOKEN]: 'facebook_conversions_api_token',
            [AdConnectionType.INSTAGRAM_PAGE]: 'instagram_page',
            [AdConnectionType.TIKTOK_AD_ACCOUNT]: 'tiktok_ad_account',
            [AdConnectionType.FACEBOOK_CONVERSION_EVENT]: 'facebook_conversion_event',
            [AdConnectionType.FACEBOOK_CUSTOM_AUDIENCE]: 'facebook_custom_audience',
            [AdConnectionType.FACEBOOK_PROFILE]: 'facebook_profile'
        };
        return typeMap[type] || type.toString();
    };

    const handleCopyFromBrand = async (sourceBrand: CurrentBrand) => {
        if (!selectedBrand || !selectedAdConnectionType) return;

        try {
            const connectionTypeString = getConnectionTypeString(selectedAdConnectionType);
            const updatedBrand = await copyConnectionBetweenBrands_api({
                targetBrandSlug: selectedBrand.slug!,
                sourceBrandSlug: sourceBrand.slug!,
                connectionType: connectionTypeString
            });

            // Update the brand in the organization brands list
            setOrganizationBrands(prevBrands => 
                prevBrands.map(brand => 
                    brand.id === selectedBrand.id ? updatedBrand : brand
                )
            );

            // Also update the current brand if it's the same
            if (selectedBrand.id === updatedBrand.id) {
                setCurrentBrand(updatedBrand as any);
            }

            // If this was a prerequisite connection (Facebook Profile), redirect to original connection
            if (originalAdConnectionType && selectedAdConnectionType === AdConnectionType.FACEBOOK_PROFILE) {
                // Small delay to allow the brand state to update
                setTimeout(() => {
                    setSelectedAdConnectionType(originalAdConnectionType);
                    setOriginalAdConnectionType(null);
                }, 100);
            }
        } catch (error) {
            console.error('Error copying connection:', error);
            throw error; // Re-throw to let AdConnectionModal handle the error display
        }
    };

    const handleAdConnectionModalClose = () => {
        // Check if we just completed a Facebook profile prerequisite
        if (originalAdConnectionType && selectedAdConnectionType === AdConnectionType.FACEBOOK_PROFILE) {
            // Redirect to the original connection they wanted
            setSelectedAdConnectionType(originalAdConnectionType);
            setOriginalAdConnectionType(null);
            // Keep modal open for the next connection
        } else {
            // Normal close
            setShowAdConnectionsModal(false);
            setSelectedAdConnectionType(null);
            setOriginalAdConnectionType(null);
            setSelectedBrand(null);
        }
    };

    const handleCloseDetailsModal = () => {
        setShowDetailsModal(false);
        setSelectedBrand(null);
        setSelectedBrandStatus(null);
    };

    const handleCloseSocialConnectModal = () => {
        setShowSocialConnectModal(false);
        setSelectedBrand(null);
        setSelectedPlatform(null);
    };



    const getPlatformIcon = (platform: SocialConnectType): string => {
        const iconMap: Record<SocialConnectType, string> = {
            [SocialConnectType.SPOTIFY]: 'spotify',
            [SocialConnectType.FACEBOOK]: 'facebook',
            [SocialConnectType.INSTAGRAM]: 'instagram',
            [SocialConnectType.TIKTOK]: 'tiktok',
            [SocialConnectType.YOUTUBE]: 'youtube',
            [SocialConnectType.TWITTER]: 'twitter',
            [SocialConnectType.SOUNDCLOUD]: 'soundcloud',
            [SocialConnectType.AUDIOMACK]: 'audiomack',
            [SocialConnectType.DEEZER]: 'deezer',
            [SocialConnectType.WEBSITE]: 'link',
            [SocialConnectType.NEWSLETTER]: 'mail',
            [SocialConnectType.SHOPIFY]: 'store',
            [SocialConnectType.ETSY]: 'etsy'
        };
        return iconMap[platform] || 'link';
    };

    const renderBrandConnectionCard = (brand: CurrentBrand, connectionStatus: BrandConnectionStatus) => {
        const platformsNeedingAttention = getPlatformsNeedingAttention(connectionStatus);
        const topMissingPlatforms = platformsNeedingAttention.slice(0, 3);

        const renderDataConnections = () => {
            const dataPlatforms: SocialConnectType[] = [
                SocialConnectType.SPOTIFY,
                SocialConnectType.AUDIOMACK,
                SocialConnectType.SOUNDCLOUD,
                SocialConnectType.TIKTOK,
                SocialConnectType.INSTAGRAM,
                SocialConnectType.YOUTUBE,
            ];

            return (
                <div className="dataConnectionsSection" style={sty.sectionContainer}>
                    <div className="sectionHeader" style={sty.sectionHeader}>
                        <div className="sectionTitle" style={sty.sectionTitle}>Data Connections</div>
                        <ButtonSymphony
                            className="editDataConnectionsButton"
                            variant="text"
                            size="small"
                            onClick={() => {
                                // Open a modal or navigate to edit data connections
                                console.log('Edit data connections for', brand.name);
                            }}
                        >
                            Edit
                        </ButtonSymphony>
                    </div>
                    <div className="dataConnectionsGrid" style={sty.dataConnectionsGrid}>
                        {dataPlatforms.map((platform) => {
                            const platformStatus = connectionStatus.platforms.find(p => p.platform === platform);
                            const isConnected = platformStatus?.status === ConnectionStatus.CONNECTED;
                            return (
                                <div
                                    key={platform}
                                    className="dataConnectionItemWrapper"
                                    style={sty.dataConnectionItemWrapper}
                                >
                                    <button
                                        className="dataConnectionItem"
                                        style={styx(
                                            sty.dataConnectionItem,
                                            isConnected ? sty.dataConnectionItemConnected : sty.dataConnectionItemDisconnected
                                        )}
                                        onClick={() => handleConnectPlatform(brand, platform)}
                                    >
                                        <Icon
                                            className="platformIcon"
                                            icon={getPlatformIcon(platform)}
                                            size={20}
                                            color={isConnected ? undefined : Colors.greyDark}
                                        />
                                    </button>
                                    {!isConnected && (
                                        <div className="addButton" style={sty.addButton}>
                                            <Icon
                                                className="addIcon"
                                                icon="plus"
                                                size={12}
                                                color={Colors.white}
                                            />
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </div>
            )
        };

        const renderAdConnections = () => {
            const items: {
                key: string;
                title: string;
                connectedName: string;
                connected: boolean;
                actionType?: AdConnectionType;
                icon: string;
                iconType?: 'default' | 'custom';
                requiredForAdReadiness?: boolean;
                connectedDescription?: string;
            }[] = [
                    {
                        key: 'fb_login',
                        title: 'Facebook Profile',
                        connectedName: brand.connections?.logged_in_fb_user?.name || 'Facebook Profile',
                        connected: Boolean(brand.connections?.logged_in_fb_user),
                        actionType: AdConnectionType.FACEBOOK_PROFILE,
                        icon: brand.connections?.logged_in_fb_user?.picture?.data?.url || 'facebook',
                        iconType: brand.connections?.logged_in_fb_user?.picture?.data?.url ? 'custom' : 'default',
                        requiredForAdReadiness: true,
                        connectedDescription: brand.connections?.logged_in_fb_user ? 'Connected to Facebook account' : undefined
                    },
                    {
                        key: 'ad_account',
                        title: 'Ad Account',
                        connectedName: brand.connections?.facebook_ad_account?.name || 'Connect your Ad Account',
                        connected: Boolean(brand.connections?.facebook_ad_account),
                        actionType: AdConnectionType.FACEBOOK_AD_ACCOUNT,
                        icon: 'facebook',
                        requiredForAdReadiness: true,
                        connectedDescription: brand.connections?.facebook_ad_account ? 
                            `$${millify(brand.connections.facebook_ad_account.amount_spent || 0)} spent • Uses ${CURRENCY_CODE_DATA[brand.connections.facebook_ad_account.currency || 'USD']?.name_plural || 'United States Dollar'} (${brand.connections.facebook_ad_account.currency || 'USD'})` 
                            : undefined
                    },
                    {
                        key: 'fb_page',
                        title: 'Facebook Page',
                        connectedName: brand.connections?.facebook_page?.name || 'Connect your Facebook Page',
                        connected: Boolean(brand.connections?.facebook_page),
                        actionType: AdConnectionType.FACEBOOK_PAGE,
                        icon: brand.connections?.facebook_page?.picture?.data?.url || 'facebook',
                        iconType: brand.connections?.facebook_page?.picture?.data?.url ? 'custom' : 'default',
                        requiredForAdReadiness: true,
                        connectedDescription: brand.connections?.facebook_page ? 
                            `${brand.connections.facebook_page.followers_count ? millify(brand.connections.facebook_page.followers_count) + ' followers' : 'Facebook page connected'}` 
                            : undefined
                    },
                    {
                        key: 'ig_page',
                        title: 'Instagram Page',
                        connectedName: brand.connections?.instagram_page?.instagramAccount?.username || 'Connect your Instagram Page',
                        connected: Boolean(brand.connections?.instagram_page),
                        actionType: AdConnectionType.INSTAGRAM_PAGE,
                        icon: brand.connections?.instagram_page?.instagramAccount?.profile_picture_url || 'instagramFlat',
                        iconType: brand.connections?.instagram_page?.instagramAccount?.profile_picture_url ? 'custom' : 'default',
                        connectedDescription: brand.connections?.instagram_page?.instagramAccount ? 
                            `${brand.connections.instagram_page.instagramAccount.followed_by_count ? millify(brand.connections.instagram_page.instagramAccount.followed_by_count) + ' followers' : 'Instagram page connected'}` 
                            : undefined
                    },
                    {
                        key: 'pixel',
                        title: 'Facebook Pixel',
                        connectedName: brand.connections?.facebook_pixel?.name ?? brand.connections?.facebook_pixel?.id ?? 'Connect your Facebook Pixel',
                        connected: Boolean(brand.connections?.facebook_pixel),
                        actionType: AdConnectionType.FACEBOOK_PIXEL,
                        icon: 'facebook',
                        connectedDescription: brand.connections?.facebook_pixel ? 'Pixel connected for conversion tracking' : undefined
                    },
                    {
                        key: 'conversions_api',
                        title: 'Conversions API Token',
                        connectedName: brand.connections?.facebook_pixel_conversions_api_access_token ? 'Your token is linked!' : 'Link your Conversions API Token',
                        connected: Boolean(brand.connections?.facebook_pixel_conversions_api_access_token),
                        actionType: AdConnectionType.FACEBOOK_CONVERSIONS_API_TOKEN,
                        icon: 'facebook',
                        connectedDescription: brand.connections?.facebook_pixel_conversions_api_access_token ? 'Server-side conversion tracking enabled' : undefined
                    },
                ];

            return (
                <div className="adConnectionsSection" style={sty.sectionContainer}>
                    <div className="sectionTitle" style={sty.sectionTitle}>Ad Connections</div>
                    <div className="adConnectionsList" style={sty.adConnectionsList}>
                        {items.map(item => (
                            <div 
                                key={item.key} 
                                className="adConnectionItem" 
                                style={styx(
                                    sty.adConnectionItem,
                                    item.connected ? sty.adConnectionItemConnected : {}
                                )}
                            >
                                <div className="adConnectionInfo" style={sty.adConnectionInfo}>
                                    <div className="adConnectionIconContainer" style={sty.adConnectionIconContainer}>
                                        <Icon
                                            className="adConnectionIcon"
                                            icon={item.icon}
                                            iconType={item.iconType || 'default'}
                                            size={24}
                                        />
                                        {item.connected && (
                                            <div className="connectionStatusOverlay" style={sty.connectionStatusOverlay}>
                                                <Icon
                                                    className="statusIcon"
                                                    icon="check"
                                                    size={12}
                                                    color={Colors.green}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <div className="adConnectionTitle" style={sty.adConnectionTitle}>
                                            {item.connected ? `${item.title}: ${item.connectedName}` : item.title}
                                        </div>
                                        {item.connected && item.connectedDescription ? (
                                            <div className="adConnectionDescription" style={sty.adConnectionDescription}>
                                                {item.connectedDescription}
                                            </div>
                                        ) : item.requiredForAdReadiness && !item.connected ? (
                                            <div className="adConnectionRequired" style={sty.adConnectionRequired}>
                                                Required to run ads with best targeting
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                                {item.actionType && (
                                    <ButtonSymphony
                                        className="connectAdItemButton"
                                        variant="text"
                                        size="small"
                                        onClick={() => handleConnectAdItem(brand, item.actionType!)}
                                    >
                                        {item.connected ? 'Edit' : 'Connect'}
                                    </ButtonSymphony>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )
        };



        return (
            <CardSymphony
                key={brand.id}
                className="brandConnectionCard"
                style={styx(sty.card, mStyle(sty, 'cardMobile'))}
                title={
                    <BrandInfo
                        brandName={brand.name}
                        brandImage={brand.image || undefined}
                        brandSlug={brand.slug || undefined}
                        titleColor={Colors.black}
                    />
                }
            >
                {renderDataConnections()}
                {renderAdConnections()}
            </CardSymphony>
        );
    };

    const renderConnectionsOverview = () => {
        if (isLoading) {
            return (
                <div className="teamConnectionsLoading" style={sty.loadingContainer}>
                    <Loader />
                </div>
            );
        }

        if (!organizationBrands.length) {
            return (
                <div className="teamConnectionsEmpty" style={sty.emptyState}>
                    <div style={sty.emptyStateText}>
                        No artists found in your team. Add artists to see their connection status.
                    </div>
                </div>
            );
        }

        return (
            <div className="teamConnectionsList" style={sty.connectionsList}>
                {organizationBrands.map((brand) => {
                    const connectionStatus = getBrandConnectionStatus(brand);
                    return renderBrandConnectionCard(brand, connectionStatus);
                })}
            </div>
        );
    };

    const renderStats = () => {
        return null;
    };

    return (
        <>
            <PageSymphony
                className={clsx('teamConnectionsPage', className)}
                isContent
            >
                <CardSymphony
                    className="teamConnectionsCard"
                    title="Social Connections"
                    subtitle="Complete overview of all artists in your team and their social platform connections. Connect missing accounts to maximize your marketing reach."
                >
                    {renderStats()}
                    {renderConnectionsOverview()}
                </CardSymphony>
            </PageSymphony>

            {/* Modals */}
            {/* {selectedBrand && selectedBrandStatus && (
                <BrandConnectionDetailsModal
                    className="teamConnectionsDetailsModal"
                    isOpen={showDetailsModal}
                    onClose={handleCloseDetailsModal}
                    brand={selectedBrand}
                    connectionStatus={selectedBrandStatus}
                />
            )} */}

            {selectedBrand && selectedPlatform && (
                <SocialConnectModal
                    className="teamConnectionsSocialConnectModal"
                    isOpen={showSocialConnectModal}
                    onClose={handleCloseSocialConnectModal}
                    socialConnectType={selectedPlatform}
                    isConnectToBrand={true}
                />
            )}

            {selectedBrand && selectedAdConnectionType && (
                <AdConnectionModal
                    className="teamConnectionsAdConnectionModal"
                    isOpen={showAdConnectionsModal}
                    onClose={handleAdConnectionModalClose}
                    connectionType={selectedAdConnectionType || undefined}
                    showCopyFromExistingBrand={true}
                    availableBrandsForCopy={organizationBrands.filter(brand => brand.id !== selectedBrand.id)}
                    onCopyFromBrand={handleCopyFromBrand}
                    isPrerequisite={Boolean(originalAdConnectionType)}
                    originalConnectionType={originalAdConnectionType || undefined}
                    brandId={selectedBrand.id}
                />
            )}
        </>
    );
};

const sty: Record<string, CSSProperties> = {
    sectionContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        marginBottom: 16,
    },
    sectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    sectionTitle: {
        ...Sty.text14bold,
        color: Colors.greyDark,
    },
    dataConnectionsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 40px)',
        gap: 12,
    },
    dataConnectionItemWrapper: {
        position: 'relative',
        width: 40,
        height: 40,
    },
    dataConnectionItem: {
        width: 40,
        height: 40,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: Colors.greyLighter,
        border: `1px solid ${Colors.greyLight}`,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    adConnectionRequired: {
        ...Sty.text14,
        color: Colors.greyDark,
    },
    adConnectionDescription: {
        ...Sty.text14,
        color: Colors.greyDark,
    },
    dataConnectionItemConnected: {
        backgroundColor: Colors.greenLight,
        border: `1px solid ${Colors.green}`,
    },
    dataConnectionItemDisconnected: {
        backgroundColor: Colors.greyLighter,
        border: `1px solid ${Colors.greyLight}`,
        filter: 'grayscale(100%)',
    },
    addButton: {
        position: 'absolute',
        bottom: -2,
        right: -2,
        width: 16,
        height: 16,
        borderRadius: '50%',
        backgroundColor: Colors.purple,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        border: `2px solid ${Colors.white}`,
    },
    connectionsList: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
        gap: 24,
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 64,
    },
    emptyState: {
        textAlign: 'center',
        padding: 64,
    },
    emptyStateText: {
        ...Sty.text16grey,
    },
    // Brand card styles
    card: {
        backgroundColor: Colors.white,
        borderRadius: 16,
        padding: 20,
        border: `1px solid ${Colors.greyLight}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.2s ease',
    },
    cardMobile: {
        padding: 16,
    },

    adReadiness: {
        backgroundColor: Colors.greyLighter,
        borderRadius: 8,
        padding: 12,
        marginBottom: 16,
    },
    adReadinessHeader: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: 8,
    },
    adReadinessText: {
        ...Sty.text14bold,
        marginLeft: 6,
    },
    adPlatforms: {
        display: 'flex',
        gap: 12,
    },
    adPlatform: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
    adConnectionsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    adConnectionItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 8,
        backgroundColor: Colors.greyLighter,
        borderRadius: 6,
    },
    adConnectionItemConnected: {
        backgroundColor: Colors.purpleLighter,
    },
    adConnectionIconContainer: {
        position: 'relative',
        width: 32,
        height: 32,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: Colors.greyLighter,
        border: `1px solid ${Colors.greyLight}`,
    },
    connectionStatusOverlay: {
        position: 'absolute',
        bottom: -2,
        right: -2,
        width: 16,
        height: 16,
        borderRadius: '50%',
        backgroundColor: Colors.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `1px solid ${Colors.greyLight}`,
    },
    adConnectionInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    adConnectionTitle: {
        ...Sty.text16,
        fontWeight: 500,
    },

};

export default TeamConnectionsPage;